import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

interface AuthSuccessStateProps {
    title: string;
    description?: string;
    icon?: LucideIcon;
    /** `success` or `error` styles the icon badge when no custom iconClassName is passed. */
    variant?: "success" | "error";
    iconClassName?: string;
    children?: React.ReactNode;
    className?: string;
}

const badgeStyles = {
    success: "bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400",
    error: "bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400",
} as const;

/** Shared success / status layout for auth completion pages. Icon sits top-right, aligned with the title. */
export function AuthSuccessState({
    title,
    description,
    icon: Icon,
    variant = "success",
    iconClassName,
    children,
    className,
}: AuthSuccessStateProps) {
    return (
        <div className={cn("flex flex-col gap-6 w-full", className)}>
            <div className="flex justify-between items-start gap-4 w-full">
                <div className="flex flex-col gap-1.5 min-w-0">
                    <h1 className="font-semibold text-foreground text-xl sm:text-2xl tracking-tight">{title}</h1>
                    {description ? (
                        <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
                    ) : null}
                </div>
                {Icon ? (
                    <div
                        className={cn(
                            "flex justify-center items-center rounded-full size-11 shrink-0",
                            iconClassName ? undefined : badgeStyles[variant]
                        )}
                    >
                        <Icon className={cn("size-5", iconClassName)} strokeWidth={2} aria-hidden />
                    </div>
                ) : null}
            </div>
            {children}
        </div>
    );
}
